import { AlertTriangle, CheckCircle2, ExternalLink } from "lucide-react";

// Honest data-quality banner. Shown at the top of every Degree page whose
// `record_evidence.confidence` is not "verified" (or lacks a URL).
//
// Why this exists
// ---------------
// Feb 2026: user caught a Wollongong-Dubai record that hand-wrote A-Level
// requirements from persona/tier assumptions instead of the university's
// actual admissions page. This banner surfaces our confidence level
// honestly so applicants know when to double-check against the official
// source. When we DO verify a record and stamp `confidence: verified`
// with a real URL, the banner disappears.
//
// Confidence tiers
// ----------------
//   verified              — record patched against the exact programme's
//                           admissions page. Banner hidden entirely.
//   verified_uni_policy   — uni-wide policy applied (tuition, IELTS, entry
//                           reqs are correct; per-programme selectivity is
//                           our editorial estimate). A subtle emerald note
//                           is shown for transparency.
//   any other / missing   — amber warning banner is shown.
//
// Props
// -----
//   verified        — boolean, from `content_verified` on the Degree doc
//   universityName  — used in the CTA
//   evidence        — the `record_evidence` object (may carry a URL to link)

export default function EvidenceBanner({ verified, universityName, evidence }) {
  const url = evidence?.url && evidence.url !== "" ? evidence.url : null;
  const confidence = evidence?.confidence;

  if (verified && confidence === "verified_uni_policy") {
    return (
      <div
        data-testid="evidence-banner-uni-policy"
        className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-3"
      >
        <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-700 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-underground-sc tracking-wider text-emerald-800">
            Uni-level verified
          </div>
          <p className="text-[12.5px] text-emerald-900 leading-snug mt-0.5">
            Tuition, English proficiency and entry-requirement rules were
            checked against{" "}
            {universityName ? <strong>{universityName}'s</strong> : "the university's"}{" "}
            official admissions page. Programme-specific selectivity is our
            editorial estimate — verify the exact offer rate against the
            programme page before applying.
          </p>
          {url && (
            <a
              data-testid="evidence-banner-source-link"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-underground-sc tracking-wider text-emerald-900 hover:underline"
            >
              View source <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>
    );
  }

  if (verified) return null;

  return (
    <div
      data-testid="evidence-banner"
      className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3"
    >
      <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-700 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-underground-sc tracking-wider text-amber-800">
          Editorial estimate — please verify
        </div>
        <p className="text-[12.5px] text-amber-900 leading-snug mt-0.5">
          The entry requirements, offer rate and cost on this page are
          editorial estimates drafted by our research team. They are a
          strong starting point — but before you apply, always confirm the
          exact numbers against{" "}
          {universityName ? <strong>{universityName}'s</strong> : "the university's"}{" "}
          official admissions page for this programme.
        </p>
        {url && (
          <a
            data-testid="evidence-banner-source-link"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-underground-sc tracking-wider text-amber-900 hover:underline"
          >
            View source cited by our team <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </div>
  );
}
